import {
    Form, Popconfirm, Switch,
    Table,
    Tooltip, notification
} from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { Icon } from "ts-react-feather-icons";
import { UserRole } from "../../constants/userRole";
import { getStoryDetail, searchStory, updateStoryActivation } from "../../helpers/api/story";
import { useUser } from "../../hooks";
import User from "../../model/User";
import { getStoryCollectionDetail } from "../../helpers/api/storyCollections";
import { CollectionConfig, StoryCollectionModel } from "./StoryCollection";

export interface StoryModel {
  key: React.Key;
  id: string;
  name: string;
  description: string;
  developmentDirection: string;
  defaultImageUrl: string;
  status: string;
  stage: string;
  createdBy: string;
  modifiedBy: string;
  createdAt: Date;
  modifiedAt: Date;
}

const StoryCollectionDetail = () => {

  const { collectionId } = useParams();

  const [story, setStory] = useState<any>(null);

  const [pageSize, setPageSize] = useState<number>(10);
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [ collection, setCollection ] = useState<StoryCollectionModel>();
  const [ collectionConfig, setCollectionConfig ] = useState<CollectionConfig[]>();
  const [stories, setStories] = useState<any[]>([]);
  const [storyDrawerVisibility, setStoryDrawerVisibility] = useState<boolean>(false);

  const currentLoggedInUser = useUser().user as User || null;
  const [api, contextHolder] = notification.useNotification();

  const [form] = Form.useForm();
  const formValueObj = Form.useWatch([], form);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);

  const showError = (errorMessage: string) => {
    api.error({
      message: `Error notification`,
      description: <span className="text-danger">{errorMessage || 'There\'re some errors occured!'}</span>,
      placement: 'topRight'
    });
  }

  useEffect(() => {
    if (!collectionId) {
      return;
    }
    getCollectionDetail(collectionId);
  }, [collectionId]);

  const getCollectionDetail = async (collectionId: string) => {
    try {
        const collection = await getStoryCollectionDetail(collectionId);
        setCollection(collection.data);
        setCollectionConfig([collection.data.config]);
        const storyIds = collection.data.storyIds;
        const stories = await getStories(storyIds);
        setStories(stories);
      } catch (err) {
        console.error(err);
        showError(err as string);
      }
  }

  const getStories = async (storyIds: string[]) => {
    try {
      const searchObj: any = {
        pageIndex: 0,
        pageSize: 10,
        storyIds
      };
      const res = await searchStory(searchObj);
      return res.data.data;
    } catch (err) {
      console.error(err);
      showError(err as string);
    }
  }

  const configColumns: ColumnsType<CollectionConfig> = [
    {
      title: "No.",
      render: (text, record, index) => {
        return (
            <span>{index + 1}</span>
        )
      }
    },
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Day min",
      render: (text, record, index) => {
        return (
          <span>{record?.config ? record.config?.dayIndexMin : ''}</span>
        )
      }
    },
    {
      title: "Day max",
      render: (text, record, index) => {
        return (
          <span>{record?.config ? record.config?.dayIndexmax : ''}</span>
        )
      }
    },
    {
      title: "Created by",
      dataIndex: "createdBy",
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      render: (text, record, index) => {
        return <span>{record?.createdAt ? new Date(record?.createdAt).toLocaleString() : ''}</span>
      }
    },
    {
      title: "Modified by",
      dataIndex: "modifiedBy",
    },
  ]

  const columns: ColumnsType<StoryModel> = [
    {
      title: "No.",
      render: (text, record, index) => {
        return (
            <span>{index + 1}</span>
        )
      }
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (text, record, index) => {
        return (
          <span>
            {record.defaultImageUrl && <img src={record.defaultImageUrl} className="image rounded" style={{ maxWidth: "35px" }} />}
            {" "}
            {record.name}
          </span>
        )
      },
    },
    {
      title: "Created by",
      dataIndex: "createdBy",
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      render: (text, record, index) => {
        return <span>{record?.createdAt ? new Date(record?.createdAt).toLocaleString() : ''}</span>
      }
    },
    {
      title: "Modified by",
      dataIndex: "modifiedBy",
    },
    {
      title: "Published?",
      dataIndex: "activated",
      render: (text, record, index) => {
        return (
          <Popconfirm
            title={`${(record.status && record.status === "active") ? "Deactivate" : "Activate"} story`}
            description={`Are you sure to ${(record.status && record.status === "active") ? "deactivate" : "activate"} this story?`}
            onConfirm={() => changeActivation(record.id, record.status)}
            okText="OK"
            cancelText="Cancel"
          >
            <Switch checked={record.status === "active"}
              disabled={
                (!currentLoggedInUser?.roles?.includes(UserRole.SUPER_ADMIN)
                  && !currentLoggedInUser?.roles?.includes(UserRole.ADMIN))
              }
            />
          </Popconfirm>
        )
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      width: 80,
      render: (text, record, index) => {
        return (
          (currentLoggedInUser?.roles?.includes(UserRole.SUPER_ADMIN) || currentLoggedInUser?.roles?.includes(UserRole.ADMIN)) && (
            <Tooltip title="Update">
              <span role="button"
                onClick={() => triggerUpdate(record.id)}
              >
                <Icon name="edit-2" color="#0d6efd" size={16} />
              </span>
            </Tooltip>
          )
        );
      },
    },
  ];

  useEffect(() => {
    search();
  }, [pageIndex]);

  // useEffect(() => {
  //   if (pageIndex === 1) {
  //     search();
  //     return;
  //   }
  //   setPageIndex(1);
  // }, [pageSize]);

  const search = async () => {
    setSearchLoading(true);
    const searchObj: any = {
      pageIndex: pageIndex - 1,
      pageSize,
    };
    try {
      if (formValueObj?.name) {
        searchObj["name"] = formValueObj?.name.trim();
      }
      const response = await searchStory(searchObj);
      if (response.status === 200) {
        const data: any[] = [];
        response.data.data.forEach((item: any, index: number) => {
          data.push({
            ...item,
            noNumber: (pageIndex - 1) * pageSize + index + 1,
          });
        });
        setStories(data);
        setTotalRecord(response.data.totalRecord);
      }
    } catch (err) {
      console.error(err);
      api.error({
        message: `Error notification`,
        description: <span className="text-danger">{err as string || 'There\'re some errors occured!'}</span>,
        placement: 'topRight'
      });
    }
    setSearchLoading(false);
  }

  const changeActivation = async (storyId: string, currentStatus: string) => {
    const story = await getStoryDetail(storyId);
    const response = await updateStoryActivation(storyId, { status: currentStatus === "active" ? "inactive" : "active" });
    if (response.status === 200) {
      api.success({
        message: `Successfull notification`,
        description: (currentStatus === "activated" ? 'Deactivate' : 'Activate') + ' story successfully!',
        placement: 'topRight'
      });
      search();
    }
  }

  const triggerUpdate = async (storyId: string) => {
    try {
      if (!storyId || storyId?.trim()?.length === 0) {
        return;
      }
      const story = await getStoryDetail(storyId);
      setStoryDrawerVisibility(true);
    } catch (err) {
      console.error(err);
      showError(err as string);
    }
  }

  return (
    <>
      <Row>
          <Col>
              <Card>
                  <Card.Body>
                      <Row>
                          <Col sm={6} className="mb-2">
                              <span className="fw-bold lead text-success">CONFIG</span>
                          </Col>
                          <Col sm={6} className="mb-2">
                              <div className="text-sm-end">
                                  <Button variant="success" className="mb-1 py-1 me-1" onClick={() => {
                                      search();
                                  }}>
                                      <i className="mdi mdi-refresh me-1"></i> Refresh
                                  </Button>
                              </div>
                          </Col>
                      </Row>

                      <Table
                          loading={searchLoading}
                          rowKey={"noNumber"}
                          className="table table-hover m-0 table-centered dt-responsive nowrap w-100"
                          dataSource={collectionConfig}
                          columns={configColumns}
                          pagination={false}
                      />

                  </Card.Body>
              </Card>
          </Col>
      </Row>
      <Row>
          <Col>
              <Card>
                  <Card.Body>
                      <Row>
                          <Col sm={6} className="mb-2">
                              <span className="fw-bold lead text-success">STORIES</span>
                          </Col>
                          <Col sm={6} className="mb-2">
                              <div className="text-sm-end">
                                  <Button variant="success" className="mb-1 py-1 me-1" onClick={() => {
                                      search();
                                  }}>
                                      <i className="mdi mdi-refresh me-1"></i> Refresh
                                  </Button>
                                  <Button variant="primary" className="mb-1 py-1 me-1" onClick={async () => {
                                  }}>
                                      <i className="mdi mdi-plus-circle me-1"></i> Add
                                  </Button>
                              </div>
                          </Col>
                      </Row>

                      <Table
                          loading={searchLoading}
                          rowKey={"noNumber"}
                          className="table table-hover m-0 table-centered dt-responsive nowrap w-100"
                          dataSource={stories}
                          columns={columns}
                          pagination={false}
                      />

                  </Card.Body>
              </Card>
          </Col>
      </Row>
    </>
  );
};

export default StoryCollectionDetail;
